import sql from "sql-template-tag";
import { z } from "zod";

import { getDb } from "~/data/sql";
import {
	type DocumentListItem,
	DocumentListItemSchema,
	type FeatureFlag,
	type Workspace,
	WorkspaceSchema,
} from "~/state/types";

export const sqliteApi = {
	documents: {
		listAll: async () => {
			const db = await getDb();

			const query = sql`
				SELECT documents.*, workspaces.id as workspaceId, workspaces.name as workspaceName
				FROM documents
				LEFT JOIN documents_workspaces ON documents.id = documents_workspaces.documentId
				LEFT JOIN workspaces ON documents_workspaces.workspaceId = workspaces.id
			`;

			const documents = await db?.select<
				{
					id: string;
					path: string;
					title: string;
					createdAt: Date;
					updatedAt: Date;
					workspaceId: string;
					workspaceName: string;
				}[]
			>(query.sql, query.values);

			return z.array(DocumentListItemSchema).parse(
				documents?.map((document) => ({
					id: document.id,
					path: document.path,
					title: document.title,
					createdAt: document.createdAt,
					updatedAt: document.updatedAt,
					workspaces: document.workspaceId
						? [{ id: document.workspaceId, name: document.workspaceName }]
						: [],
				}))
			);
		},

		add: async (document: DocumentListItem) => {
			const db = await getDb();

			const query = sql`
				INSERT INTO documents (id, path, title, createdAt, updatedAt) VALUES (${document.id}, ${document.path}, ${document.title}, ${document.createdAt}, ${document.updatedAt})
			`;

			await db?.execute(query.sql, query.values);
		},

		upsert: async (document: DocumentListItem) => {
			const db = await getDb();

			const query = sql`
				INSERT INTO documents (id, path, title, createdAt, updatedAt) VALUES (${document.id}, ${document.path}, ${document.title}, ${document.createdAt}, ${document.updatedAt})
				ON CONFLICT (id) DO UPDATE SET path = ${document.path}, title = ${document.title}, createdAt = ${document.createdAt}, updatedAt = ${document.updatedAt}
			`;

			await db?.execute(query.sql, query.values);
		},

		updatePath: async (documentId: string, path: string) => {
			const db = await getDb();

			const query = sql`
				UPDATE documents SET path = ${path} WHERE id = ${documentId}
			`;

			await db?.execute(query.sql, query.values);
		},

		delete: async (documentId: string) => {
			const db = await getDb();

			const query = sql`
				DELETE FROM documents WHERE id = ${documentId}
			`;

			await db?.execute(query.sql, query.values);
		},
	},

	documentWorkspaces: {
		upsert: async (documentId: string, workspaceId: string) => {
			const db = await getDb();

			const query = sql`
				INSERT INTO documents_workspaces (documentId, workspaceId) VALUES (${documentId}, ${workspaceId})
				ON CONFLICT (documentId, workspaceId) DO NOTHING
			`;

			await db?.execute(query.sql, query.values);
		},
		deleteByDocumentId: async (documentId: string) => {
			const db = await getDb();

			const query = sql`
				DELETE FROM documents_workspaces WHERE documentId = ${documentId}
			`;

			await db?.execute(query.sql, query.values);
		},
	},

	featureFlags: {
		listAll: async () => {
			const db = await getDb();
			const query = sql`SELECT * FROM feature_flags`;

			return db?.select<{ id: string; enabled: string }[]>(query.sql, query.values);
		},

		upsert: async (flag: FeatureFlag) => {
			const db = await getDb();

			const query = sql`
				INSERT INTO feature_flags (id, enabled) VALUES (${flag.id}, ${flag.enabled})
				ON CONFLICT (id) DO UPDATE SET enabled = ${flag.enabled}
			`;

			await db?.execute(query.sql, query.values);
		},
	},

	workspaces: {
		listAll: async () => {
			const db = await getDb();

			const query = sql`SELECT * FROM workspaces`;

			const workspaces = await db?.select<Workspace[]>(query.sql, query.values);

			return z.array(WorkspaceSchema).parse(workspaces);
		},

		upsert: async (workspace: Workspace) => {
			const db = await getDb();

			const query = sql`
				INSERT INTO workspaces (id, name, host, slug) VALUES (${workspace.id}, ${workspace.name}, ${workspace.host}, ${workspace.slug})
				ON CONFLICT (id) DO UPDATE SET name = ${workspace.name}, host = ${workspace.host}, slug = ${workspace.slug}
			`;

			await db?.execute(query.sql, query.values);
		},
	},
};
